// Pre-configured project templates by industry
export const PROJECT_TEMPLATES = {
  manufacturing: [
    {
      id: 'mfg_cycle_time',
      title: 'Cycle Time Reduction',
      description: 'Reduce manufacturing cycle time through process optimization and waste elimination',
      industry: 'Manufacturing',
      defaultProblem: 'Current manufacturing cycle time exceeds target by 20%, causing delivery delays and increased WIP inventory',
      suggestedMetrics: ['Cycle Time (mins)', 'Throughput (units/hr)', 'WIP Inventory', 'On-time Delivery %'],
      suggestedScope: 'Production line A, all shifts, Q1-Q2 2026',
      typicalDuration: '3-6 months',
      commonRootCauses: ['Equipment changeover time', 'Material waiting time', 'Process bottlenecks', 'Quality rework'],
    },
    {
      id: 'mfg_defect_reduction',
      title: 'Defect Rate Reduction',
      description: 'Reduce product defects and improve first-pass yield through quality improvements',
      industry: 'Manufacturing',
      defaultProblem: 'Defect rate of 5% is causing $50K monthly in scrap costs and customer complaints',
      suggestedMetrics: ['Defect Rate (%)', 'First Pass Yield', 'Scrap Cost ($)', 'Customer Returns'],
      suggestedScope: 'Assembly department, Product family X',
      typicalDuration: '4-6 months',
      commonRootCauses: ['Operator training gaps', 'Material variation', 'Equipment calibration', 'Process parameters'],
    },
    {
      id: 'mfg_oee',
      title: 'OEE Improvement',
      description: 'Improve Overall Equipment Effectiveness through availability, performance, and quality',
      industry: 'Manufacturing',
      defaultProblem: 'Current OEE of 65% is below world-class target of 85%, impacting production capacity',
      suggestedMetrics: ['OEE (%)', 'Availability', 'Performance', 'Quality Rate'],
      suggestedScope: 'Critical equipment lines, all shifts',
      typicalDuration: '6-9 months',
      commonRootCauses: ['Unplanned downtime', 'Minor stoppages', 'Speed losses', 'Startup rejects'],
    },
  ],
  healthcare: [
    {
      id: 'hc_wait_time',
      title: 'Patient Wait Time Reduction',
      description: 'Reduce patient waiting times to improve satisfaction and throughput',
      industry: 'Healthcare',
      defaultProblem: 'Average patient wait time of 45 minutes exceeds target of 15 minutes, impacting satisfaction scores',
      suggestedMetrics: ['Wait Time (mins)', 'Patient Satisfaction Score', 'Daily Patient Volume', 'Staff Utilization'],
      suggestedScope: 'Outpatient clinic, weekday operations',
      typicalDuration: '3-4 months',
      commonRootCauses: ['Scheduling inefficiency', 'Documentation delays', 'Resource bottlenecks', 'No-show rate'],
    },
    {
      id: 'hc_medication_errors',
      title: 'Medication Error Reduction',
      description: 'Reduce medication administration errors through process standardization',
      industry: 'Healthcare',
      defaultProblem: 'Medication error rate of 2% poses patient safety risk and regulatory concerns',
      suggestedMetrics: ['Error Rate (%)', 'Near Miss Events', 'Adverse Events', 'Compliance Rate'],
      suggestedScope: 'Inpatient pharmacy and nursing units',
      typicalDuration: '4-6 months',
      commonRootCauses: ['Transcription errors', 'Look-alike medications', 'Handoff failures', 'Alert fatigue'],
    },
    {
      id: 'hc_readmission',
      title: 'Hospital Readmission Reduction',
      description: 'Reduce 30-day readmission rates through improved discharge processes',
      industry: 'Healthcare',
      defaultProblem: '15% readmission rate exceeds CMS target, resulting in penalties and poor outcomes',
      suggestedMetrics: ['30-day Readmission Rate', 'Length of Stay', 'Discharge Instructions Compliance', 'Follow-up Rate'],
      suggestedScope: 'CHF and COPD patient populations',
      typicalDuration: '6-9 months',
      commonRootCauses: ['Inadequate discharge planning', 'Medication non-adherence', 'Follow-up gaps', 'Patient education'],
    },
  ],
  finance: [
    {
      id: 'fin_cycle_time',
      title: 'Loan Processing Time Reduction',
      description: 'Reduce loan processing cycle time while maintaining quality and compliance',
      industry: 'Finance',
      defaultProblem: 'Loan processing takes 10 days on average vs. industry benchmark of 3 days',
      suggestedMetrics: ['Processing Time (days)', 'First-time Approval Rate', 'Error Rate', 'Customer Satisfaction'],
      suggestedScope: 'Personal loan applications, standard products',
      typicalDuration: '3-5 months',
      commonRootCauses: ['Manual data entry', 'Multiple handoffs', 'Document chase', 'Approval bottlenecks'],
    },
    {
      id: 'fin_error_reduction',
      title: 'Transaction Error Reduction',
      description: 'Reduce financial transaction errors and associated correction costs',
      industry: 'Finance',
      defaultProblem: 'Transaction error rate of 3% results in $100K monthly correction costs and customer complaints',
      suggestedMetrics: ['Error Rate (%)', 'Correction Cost ($)', 'Customer Complaints', 'Straight-through Processing Rate'],
      suggestedScope: 'Wire transfers and ACH transactions',
      typicalDuration: '4-6 months',
      commonRootCauses: ['Data entry errors', 'System integration issues', 'Validation gaps', 'Training deficiencies'],
    },
  ],
  technology: [
    {
      id: 'tech_deployment',
      title: 'Deployment Frequency Improvement',
      description: 'Increase deployment frequency while reducing deployment failures',
      industry: 'Technology',
      defaultProblem: 'Monthly deployments with 15% failure rate causing extended downtime and delayed features',
      suggestedMetrics: ['Deployment Frequency', 'Failure Rate', 'Mean Time to Recovery', 'Lead Time for Changes'],
      suggestedScope: 'Production deployment pipeline, core services',
      typicalDuration: '3-6 months',
      commonRootCauses: ['Manual deployment steps', 'Environment inconsistency', 'Testing gaps', 'Rollback complexity'],
    },
    {
      id: 'tech_incident',
      title: 'Incident Response Improvement',
      description: 'Reduce incident response and resolution time for critical systems',
      industry: 'Technology',
      defaultProblem: 'Mean time to resolve critical incidents is 4 hours vs. SLA target of 1 hour',
      suggestedMetrics: ['MTTR (hours)', 'MTTD (mins)', 'Incident Count', 'Customer Impact Duration'],
      suggestedScope: 'Tier 1 production systems',
      typicalDuration: '2-4 months',
      commonRootCauses: ['Alert noise', 'Runbook gaps', 'Escalation delays', 'Knowledge silos'],
    },
    {
      id: 'tech_defect',
      title: 'Software Defect Reduction',
      description: 'Reduce production defects through improved development practices',
      industry: 'Technology',
      defaultProblem: 'Defect escape rate of 20% to production causing support burden and customer impact',
      suggestedMetrics: ['Defect Escape Rate', 'Defects per Release', 'Code Coverage', 'Customer-reported Bugs'],
      suggestedScope: 'Core product development process',
      typicalDuration: '4-6 months',
      commonRootCauses: ['Insufficient testing', 'Code review gaps', 'Requirements ambiguity', 'Technical debt'],
    },
  ],
  services: [
    {
      id: 'svc_response_time',
      title: 'Service Response Time Improvement',
      description: 'Reduce customer service response and resolution times',
      industry: 'Services',
      defaultProblem: 'Average first response time of 24 hours exceeds target of 4 hours, impacting CSAT',
      suggestedMetrics: ['First Response Time', 'Resolution Time', 'CSAT Score', 'Ticket Volume'],
      suggestedScope: 'Customer support operations, all channels',
      typicalDuration: '2-4 months',
      commonRootCauses: ['Routing inefficiency', 'Knowledge gaps', 'Tool limitations', 'Staffing imbalance'],
    },
    {
      id: 'svc_fcr',
      title: 'First Contact Resolution Improvement',
      description: 'Increase first contact resolution rate to reduce repeat contacts',
      industry: 'Services',
      defaultProblem: 'FCR rate of 60% results in high repeat contact rate and increased operational costs',
      suggestedMetrics: ['FCR Rate (%)', 'Repeat Contact Rate', 'Agent Utilization', 'Cost per Contact'],
      suggestedScope: 'Tier 1 support, phone and chat channels',
      typicalDuration: '3-5 months',
      commonRootCauses: ['Incomplete resolution', 'Knowledge base gaps', 'Agent training', 'System limitations'],
    },
  ],
};

export const getTemplatesByIndustry = (industry) => {
  const normalizedIndustry = industry?.toLowerCase().replace(/\s+/g, '_');
  return PROJECT_TEMPLATES[normalizedIndustry] || [];
};

export const getAllTemplates = () => {
  return Object.values(PROJECT_TEMPLATES).flat();
};

export const getTemplateById = (templateId) => {
  const allTemplates = getAllTemplates();
  return allTemplates.find(t => t.id === templateId);
};
